import { RefObject, useRef } from 'react'

import {
  Box,
  Group,
  ItemSkeleton,
  useVirtualViewport,
  Widget,
} from '@revolut-internal/ui-kit'

import { InfiniteLoader } from 'components/InfiniteLoader/InfiniteLoader'
import { CustomPlaceholder as Placeholder } from 'components/Placeholder/Placeholder'
import { AgentAttendanceShort, Ticket } from 'types'

import { ResourcesPanelAgentDetails } from './ResourcesPanelAgentDetails'
import { ResourcesPanelTicket } from './ResourcesPanelTicket'

type Resource = Ticket | AgentAttendanceShort
type ResourcesPanelListProps = {
  resources: Resource[]
  entityName: string
  scrollRef: RefObject<HTMLDivElement | null>
  fetchNextPage?: VoidFunction
  keyExtractor: (index: number) => number | string
  isPending: boolean
  hasNextPage?: boolean
}

const ITEM_HEIGHT = 64
const OVERSCAN = 16
const estimateVirtualItemSize = () => ITEM_HEIGHT

export const ResourcesPanelList = ({
  resources,
  entityName,
  fetchNextPage,
  keyExtractor,
  scrollRef,
  isPending,
  hasNextPage,
}: ResourcesPanelListProps) => {
  const parentRef = useRef(null)
  const virtualList = useVirtualViewport({
    parentRef,
    scrollRef,
    size: resources?.length ?? 0,
    estimateSize: estimateVirtualItemSize,
    keyExtractor,
    overscan: OVERSCAN,
  })

  const hasData = resources?.length && virtualList.virtualItems.length

  if (isPending) {
    return (
      <Group ref={parentRef}>
        <ItemSkeleton />
        <ItemSkeleton />
        <ItemSkeleton />
      </Group>
    )
  }

  if (!hasData) {
    return (
      <Widget ref={parentRef}>
        <Placeholder icon="Binoculars" title={`No ${entityName}s found`} />
      </Widget>
    )
  }

  return (
    <>
      <Group
        ref={parentRef}
        style={{
          position: 'relative',
          height: `${virtualList.totalSize}px`,
        }}
      >
        {virtualList.virtualItems.map(virtual => {
          const item = resources[virtual.index]

          return (
            <Box
              key={keyExtractor(virtual.index)}
              ref={virtual.measureRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                minHeight: `${virtual.size}px`,
                transform: `translateY(${virtual.start}px)`,
              }}
            >
              {'uri' in item ? (
                <ResourcesPanelTicket ticket={item} />
              ) : (
                <ResourcesPanelAgentDetails agentDetails={item} />
              )}
            </Box>
          )
        })}
      </Group>

      {fetchNextPage && (
        <InfiniteLoader
          isPending={isPending}
          hasMore={hasNextPage}
          onLoadMore={fetchNextPage}
        >
          <ItemSkeleton />
        </InfiniteLoader>
      )}
    </>
  )
}
